import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaExclamationTriangle, FaSyncAlt } from 'react-icons/fa';
import { useAppSelector } from '../../hooks';
import { DataProcessingQueueEntry } from '../../upstreamApi/apiActions';
import { selectLoadStartedAt, selectLoadUpdatedAt, selectLoadStatus } from '../../selectors';

const STORAGE_KEY = 'platformBannerCollapsed';

function mapLoadStatus(status: string | null | undefined): string {
  if (!status) return 'Recalculating aggregates and rebuilding indexes.';
  if (status === 'pending') return 'Preparing for update...';
  const key = status.replace(/^step \d+: /, '');
  if (key.startsWith('importing')) return 'Rebuilding search indexes...';
  if (key.startsWith('populating') || key.startsWith('deriving') || key.startsWith('updating')) {
    return 'Recalculating aggregates and statistics...';
  }
  return 'Recalculating aggregates and rebuilding indexes.';
}

export default function PlatformUpdateBanner() {
  const queue = useAppSelector((state) => state.auth.dataProcessingQueue);
  const loadStartedAt = useAppSelector(selectLoadStartedAt);
  const loadUpdatedAt = useAppSelector(selectLoadUpdatedAt);
  const loadStatus = useAppSelector(selectLoadStatus);

  const processingCount = queue?.find((e: DataProcessingQueueEntry) => e.status === 'processing')?.status_count ?? 0;
  const stagingCount = queue?.find((e: DataProcessingQueueEntry) => ['staging', 'staged'].includes(e.status))?.status_count ?? 0;
  const retrievedCount = queue?.find((e: DataProcessingQueueEntry) => e.status === 'retrieved')?.status_count ?? 0;
  const isDataProcessing = processingCount > 0;
  const isLoadRunning = !!loadStartedAt;
  const isBannerActive = isDataProcessing || isLoadRunning;

  const latestUpdatedValues = queue
    ?.filter((e: DataProcessingQueueEntry) => ['processing', 'staging', 'staged', 'processed'].includes(e.status))
    .map((e: DataProcessingQueueEntry) => e.latest_updated_on)
    .filter((d: string | null): d is string => d !== null)
    .sort();

  const queueUpdatedDate = latestUpdatedValues?.[latestUpdatedValues.length - 1]
    ? new Date(latestUpdatedValues[latestUpdatedValues.length - 1].replace(' ', 'T'))
    : null;

  const effectiveUpdatedDate = isLoadRunning ? (loadUpdatedAt ?? null) : queueUpdatedDate;

  const getRelativeTime = useCallback(() => {
    if (!effectiveUpdatedDate) return null;
    const diffMs = Date.now() - effectiveUpdatedDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs === 1) return '1 hour ago';
    if (diffHrs < 24) return `${diffHrs} hours ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }, [effectiveUpdatedDate]);

  const [relativeTime, setRelativeTime] = useState(getRelativeTime);

  useEffect(() => {
    setRelativeTime(getRelativeTime());
    const interval = setInterval(() => setRelativeTime(getRelativeTime()), 60000);
    return () => clearInterval(interval);
  }, [getRelativeTime]);

  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });

  const prevActiveRef = useRef<boolean | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isBannerActive;

    if (isBannerActive) {
      if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; }
      setIsExiting(false);
      setIsVisible(true);
    } else if (wasActive === true) {
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, 350);
    }

    return () => { if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; } };
  }, [isBannerActive]);

  const handleToggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* ignore */ }
  };

  if (!isVisible) return null;

  const bannerClass = [
    'platform-update-banner',
    isExiting ? 'platform-update-banner--exiting' : '',
    isCollapsed ? 'platform-update-banner--collapsed' : '',
  ].filter(Boolean).join(' ');

  if (isCollapsed) {
    return (
      <button
        type="button"
        className={bannerClass}
        onClick={handleToggle}
        aria-label="Expand processing status banner"
      >
        <div className="platform-update-banner__header">
          <FaSyncAlt className="platform-update-banner__warning-icon" />
          <FaChevronUp className="platform-update-banner__toggle" />
        </div>
      </button>
    );
  }

  const totalCount = processingCount + stagingCount;

  const metaText = isLoadRunning
    ? mapLoadStatus(loadStatus)
    : retrievedCount > 0
      ? `Processing ${totalCount.toLocaleString()} sources, ${retrievedCount.toLocaleString()} to go`
      : `Processing ${totalCount.toLocaleString()} sources`;

  return (
    <div className={bannerClass}>
      <div className="platform-update-banner__header">
        <span className="platform-update-banner__label">
          <FaExclamationTriangle />
          Upstream Update
        </span>
        <button
          className="platform-update-banner__toggle"
          onClick={handleToggle}
          aria-label="Collapse processing status banner"
          title="Collapse"
        >
          <FaChevronDown />
        </button>
      </div>
      <p className="platform-update-banner__message">
        We're rolling out a platform update! Some exports may fail during this time, if so, please try again shortly.
      </p>
      <div className="platform-update-banner__footer">
        <FaSyncAlt className="platform-update-banner__icon" />
        <span className="platform-update-banner__meta">
          {metaText}{relativeTime ? ` · updated ${relativeTime}` : ''}
        </span>
      </div>
    </div>
  );
}